import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiPhone, FiMail, FiMapPin, FiShoppingBag } from 'react-icons/fi';
import PageModal from './PageModal';
import { getApiUrl } from '../utils/api';
import { useQuote } from '../contexts/QuoteContext';

const Header = ({ onOpenModal }) => {
  const { quoteItems } = useQuote();
  const quoteItemCount = quoteItems.length;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  const [pageModal, setPageModal] = useState({ isOpen: false, slug: null });
  const [topMenuPages, setTopMenuPages] = useState([]);

  // Handle scroll effect and active section
  useEffect(() => {
    fetchTopMenuPages();

    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      setIsAtTop(window.scrollY <= 150);

      // Determine active section
      const sections = navigationItems.map(item => item.href.slice(1));
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (currentSection) {
        setActiveSection(currentSection);
      }

      console.log('[DEBUG] Header scroll state:', scrolled, 'scrollY:', window.scrollY, 'activeSection:', currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchTopMenuPages = async () => {
    try {
      const response = await fetch(getApiUrl('pages/menu/top'));
      if (response.ok) {
        const data = await response.json();
        setTopMenuPages(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching top menu pages:', error);
      setTopMenuPages([]);
    }
  };

  const openPageModal = (slug) => {
    setPageModal({ isOpen: true, slug });
  };

  const closePageModal = () => {
    setPageModal({ isOpen: false, slug: null });
  };

  // Base navigation items
  const baseNavigationItems = [
    { name: 'Home', href: '#home', index: 1 },
    { name: 'Products', href: '#products', index: 2 },
    { name: 'About', href: '#about', index: 3 },
    { name: 'Testimonials', href: '#testimonials', index: 4 },
    { name: 'FAQs', href: '#faqs', index: 5 },
  ];

  // Dynamic navigation items from pages
  const pageNavigationItems = topMenuPages.map((page, index) => ({
    name: page.name || page.title,
    href: `page-${page.slug}`,
    index: baseNavigationItems.length + index + 1,
    isPage: true,
    slug: page.slug
  }));

  // Combined navigation items
  const navigationItems = [...baseNavigationItems, ...pageNavigationItems];

  // Contact information for top bar
  const contactInfo = [
    { icon: FiPhone, text: '0466 645 060', href: 'tel:+61466645060' },
    { icon: FiMail, text: 'hire@melbournefloralhire.com.au', href: 'mailto:hire@melbournefloralhire.com.au' },
    { icon: FiMapPin, text: 'Noble Park, VIC', href: '#location' },
  ];

  return (
    <>
      {/* Main Header */}
      <header
        className={`fixed top-0 w-full z-50 ${isAtTop ? 'bg-transparent' : 'bg-[#C4BBAF]'} transition-all duration-300`}
      >
        {/* Top Contact Bar */}
        <div className="py-2 bg-black text-white">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-center text-sm">
              <div className="flex gap-6">
                {contactInfo.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    className="flex items-center gap-2 hover:text-primary-300 transition-colors"
                  >
                    <item.icon size={14} />
                    <span>{item.text}</span>
                  </a>
                ))}
              </div>
              <div className="hidden md:block">
                <span className="text-primary-300">Premium Floral Arrangements for Hire</span>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-6">
            {/* Logo */}
            <Link href="/" className="flex items-center">
              <Image
                src={isAtTop ? "/images/logo_white.png" : "/images/logo.png"}
                alt="Melbourne Floral Hire"
                width={273}
                height={120}
                className="h-20 w-auto object-contain"
                fetchPriority="auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems.map((item) => {
                const isActive = activeSection === item.href.slice(1);
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      if (item.isPage) {
                        openPageModal(item.slug);
                      } else {
                        const element = document.getElementById(item.href.slice(1));
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                    className={`${isAtTop ? 'text-white' : 'text-black'} hover:text-primary-200 font-medium transition-colors relative group`}
                  >
                    {item.name}
                    <span className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-300 ${isActive ? `w-full ${isAtTop ? 'bg-white' : 'bg-[#4E3524]/90'}` : `w-0 ${isAtTop ? 'bg-white' : 'bg-primary-500'} group-hover:w-full`}`}></span>
                  </button>
                );
              })}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="btn bg-[#FFA6C9] hover:bg-[#FFA6C9]/80 text-white"
              >
                Contact Us
              </button>
              <button
                onClick={() => onOpenModal('contact')}
                className="btn bg-accent-purple-500 hover:bg-accent-purple-600 text-white"
              >
                Book Consultation
              </button>
              <button
                onClick={() => onOpenModal('quote')}
                className="relative p-3 rounded-lg bg-accent-purple-500 hover:bg-accent-purple-600 text-white transition-colors"
                aria-label={`Open quote (${quoteItemCount} items)`}
              >
                <FiShoppingBag size={20} />
                {quoteItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FFA6C9] text-white text-xs font-bold flex items-center justify-center">
                    {quoteItemCount}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Basket + Menu Button */}
            <div className="flex items-center gap-2 lg:hidden">
              <button
                onClick={() => onOpenModal('quote')}
                className="relative p-2 rounded-lg bg-accent-purple-500 text-white transition-colors"
                aria-label={`Open quote (${quoteItemCount} items)`}
              >
                <FiShoppingBag size={20} />
                {quoteItemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#FFA6C9] text-white text-xs font-bold flex items-center justify-center">
                    {quoteItemCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`${isAtTop ? 'text-white' : 'text-black'} hover:bg-black/10 p-2 rounded-lg transition-colors`}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#C4BBAF] border-t shadow-lg overflow-hidden"
          >
            <div className="container mx-auto px-4 py-6">
              <nav className="flex flex-col space-y-4">
                {navigationItems.map((item) => {
                  const isActive = activeSection === item.href.slice(1);
                  return (
                    <button
                      key={item.name}
                      onClick={() => {
                        if (item.isPage) {
                          openPageModal(item.slug);
                        } else {
                          const element = document.getElementById(item.href.slice(1));
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth' });
                          }
                        }
                        setIsMenuOpen(false);
                      }}
                      className={`font-medium py-2 transition-colors text-left relative ${isActive ? 'text-[#4E3524]' : 'text-neutral-700 hover:text-primary-600'}`}
                    >
                      {item.name}
                      {isActive && <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#4E3524]/90"></span>}
                    </button>
                  );
                })}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      const element = document.getElementById('contact');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                      setIsMenuOpen(false);
                    }}
                    className="btn bg-[#FFA6C9] hover:bg-[#FFA6C9]/80 text-white w-full"
                  >
                    Contact Us
                  </button>
                  <button
                    onClick={() => {
                      onOpenModal('contact');
                      setIsMenuOpen(false);
                    }}
                    className="btn bg-accent-purple-500 hover:bg-accent-purple-600 text-white w-full"
                  >
                    Book Consultation
                  </button>
                  <button
                    onClick={() => {
                      onOpenModal('quote');
                      setIsMenuOpen(false);
                    }}
                    className="btn bg-accent-purple-500 hover:bg-accent-purple-600 text-white w-full flex items-center justify-center gap-2"
                    aria-label={`Open quote (${quoteItemCount} items)`}
                  >
                    <FiShoppingBag size={18} />
                    View Quote {quoteItemCount > 0 && `(${quoteItemCount})`}
                  </button>
                </div>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page Modal */}
      <PageModal
        isOpen={pageModal.isOpen}
        onClose={closePageModal}
        slug={pageModal.slug}
      />
    </>
  );
};

export default Header;